// ---------------------------------------------------------------------------
// LanguageSwitcher Renderer (editor key: LanguageSwitcher)
// ---------------------------------------------------------------------------
// i18n locale picker rendered as a semantic `<nav>` containing a list of
// language links. Editor input is a pipe-delimited textarea
// ("locale | Label | /path" per line). Locale codes are constrained to
// BCP-47-ish patterns (`en`, `en-US`, `zh-Hans-CN`) so we can safely emit
// `hreflang` and `lang` attributes; anything else falls back to a bare
// link. Hrefs run through the URL safelist. The optional `current` prop
// marks the active row with `aria-current="true"` and removes its link.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface LanguageSwitcherProps {
  locales?: string;
  current?: string;
  ariaLabel?: string;
}

interface LocaleEntry {
  code: string;
  label: string;
  href: string;
}

const LOCALE_PATTERN = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;

function parseLocales(value: unknown): LocaleEntry[] {
  if (typeof value !== "string") return [];
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [codePart, labelPart, hrefPart] = line.split("|");
      return {
        code: (codePart ?? "").trim(),
        label: (labelPart ?? "").trim(),
        href: (hrefPart ?? "").trim(),
      };
    })
    .filter((entry) => entry.code.length > 0 && entry.label.length > 0);
}

function isValidLocale(code: string): boolean {
  return LOCALE_PATTERN.test(code);
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as LanguageSwitcherProps;

  const locales = parseLocales(p.locales);
  if (locales.length === 0) {
    return "<!-- language-switcher missing locales -->";
  }

  const current = typeof p.current === "string" ? p.current.trim() : "";
  const ariaLabel =
    typeof p.ariaLabel === "string" && p.ariaLabel.trim()
      ? p.ariaLabel.trim()
      : "Language";

  const lis = locales
    .map((entry) => {
      const validCode = isValidLocale(entry.code);
      const isCurrent = validCode && current && entry.code === current;
      const langAttr = validCode ? ` lang="${attr(entry.code)}"` : "";
      const hreflangAttr = validCode ? ` hreflang="${attr(entry.code)}"` : "";

      if (isCurrent || !entry.href) {
        const currentAttr = isCurrent ? ' aria-current="true"' : "";
        return `    <li class="ease-language-switcher__item">\n      <span class="ease-language-switcher__label"${langAttr}${currentAttr}>${text(entry.label)}</span>\n    </li>`;
      }
      return `    <li class="ease-language-switcher__item">\n      <a class="ease-language-switcher__link"${langAttr}${hreflangAttr} href="${url(entry.href)}">${text(entry.label)}</a>\n    </li>`;
    })
    .join("\n");

  return `<nav class="ease-language-switcher" aria-label="${attr(ariaLabel)}">
  <ul class="ease-language-switcher__list" role="list">
${lis}
  </ul>
</nav>`;
}

registerRenderer("language-switcher", { render });
